import React, { useState } from "react";
import { DataTable, DataPagination } from "@app/components/atoms";
import { IconButton, Typography } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import CreateTOUDialog from "./CreateTOUDialog";
import { useTranslation } from "react-i18next";
import { format } from "date-fns";
import { LIMIT_PAGE } from "@app/constants";
import { ManageTOUTableProps } from "@app/types/tou.types";

const ManageTOUTable: React.FC<ManageTOUTableProps> = ({
  touData,
  loading = false,
  timeFormat = "yyyy/MM/dd HH:mm",
  field,
  setField,
  order,
  setOrder,
  getTOUs,
  pageInfo = { page: 1, limit: LIMIT_PAGE, total: 0 },
  setPageInfo = () => {},
}) => {
  const { t } = useTranslation();
  const rows = touData ?? [];

  const extractPdfPath = (fullUrl?: string) => {
    if (!fullUrl) return "";
    try {
      const u = new URL(fullUrl);
      const path = u.pathname.replace(/^\/+/, "");
      if (path.startsWith("publish/")) return path.replace(/^publish\//, "");
      return path;
    } catch (e) {
      const m = fullUrl.match(/\/publish\/(.+)$/);
      if (m && m[1]) return m[1];
      return fullUrl;
    }
  };

  const columns = [
    {
      key: "country",
      title: t("license.countryCode"),
      dataIndex: "residenceCountryCode",
      sorted: true,
      align: "center",
      className: "pl-0 txt-pre-wrap",
      whiteSpace: "nowrap",
      render: (value: string, record: any) => (
        <Typography>{record.countryName || value}</Typography>
      ),
      style: { minWidth: 120 },
    },
    {
      key: "version",
      title: t("version"),
      dataIndex: "version",
      sorted: true,
      whiteSpace: "nowrap",
      className: "txt-pre-wrap",
      align: "center",
      style: { minWidth: 80 },
    },
    {
      key: "type",
      title: t("subType"),
      dataIndex: "type",
      sorted: true,
      whiteSpace: "nowrap",
      className: "txt-pre-wrap",
      align: "left",
      style: { minWidth: 80 },
    },
    {
      key: "pdfUrl",
      title: t("pdfUrl"),
      dataIndex: "pdfUrl",
      sorted: true,
      whiteSpace: "nowrap",
      className: "txt-pre-wrap",
      render: (value: string) =>
        value ? (
          <a
            href={value}
            target="_blank"
            rel="noreferrer"
            style={{
              color: "#1976d2",
              textDecoration: "underline",
              cursor: "pointer",
            }}
          >
            {value}
          </a>
        ) : (
          <span />
        ),
      style: { minWidth: 160 },
    },
    {
      key: "isoLocaleCode",
      title: t("isoLocaleCode"),
      dataIndex: "isoLocaleCode",
      whiteSpace: "nowrap",
      className: "txt-pre-wrap",
      align: "center",
      sorted: true,
      style: { minWidth: 100 },
    },

    {
      key: "createdAt",
      title: t("announcementTable.createdTime"),
      dataIndex: "createdAt",
      whiteSpace: "nowrap",
      sorted: true,
      align: "center",
      className: "txt-pre-wrap",
      render: (value: string) =>
        value ? (
          <span className="txt-nowrap">
            {format(new Date(value), timeFormat)}
          </span>
        ) : (
          <span />
        ),
      style: { minWidth: 140 },
    },
    {
      key: "edit",
      title: t("formAction.edit"),
      dataIndex: "edit",
      whiteSpace: "nowrap",
      className: "txt-pre-wrap",
      align: "center",
      forceRender: true,
      render: (_: any, record: any) => (
        <IconButton
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            const pdf = record?.pdfUrl || "";
            const filteredPdf = extractPdfPath(pdf);
            setEditData({ ...record, pdfUrl: filteredPdf });
            setOpenEdit(true);
          }}
          aria-label="Edit TOU"
        >
          <EditIcon fontSize="small" />
        </IconButton>
      ),
      style: { minWidth: 80 },
    },
  ];

  const [openEdit, setOpenEdit] = useState(false);
  const [editData, setEditData] = useState<any>(null);

  const handleChangePage = (page: number) => {
    setPageInfo({ ...pageInfo, page });
  };

  const handleSort = (o: string | undefined, property: string) => {
    if (setOrder) setOrder(o);
    if (setField) setField(property);
  };

  return (
    <>
      <>
        <DataTable
          rowKey="touVersionMasterId"
          columns={columns}
          dataSource={rows}
          textEmptyData={t("noTOUFound")}
          onRequestSort={handleSort}
          loading={loading}
        />

        <DataPagination
          isHidden={!rows?.length}
          className="mt-25 sp-p24"
          currentPage={pageInfo.page}
          limitPage={pageInfo.limit}
          totalPage={pageInfo.total}
          handleChangePage={handleChangePage}
        />

        <CreateTOUDialog
          open={openEdit}
          setOpen={setOpenEdit}
          initialData={editData}
          onSuccess={() => {
            if (getTOUs) getTOUs();
          }}
        />
      </>
    </>
  );
};

export default ManageTOUTable;
